import chroma from 'chroma-js';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { Fill, Stroke, Style } from 'ol/style';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import CircleStyle from 'ol/style/Circle';
import { config } from 'config';

/**
 * Creates the circles for the map.
 * @module layers/circle
 * @type {Object}
 */
export const circle = {

  /**
   * @type {JsonStatReader}
   */
  _reader: null,

  /**
   * The layer containing the circles
   * @type {VectorLayer}
   */
  layer: null,

  /**
   * Creates the circle layer.
   * @returns {circle}
   * @param {JsonStatReader} jsonStatReader
   * @param {GeoJSON} centerPoints define where to put the circles
   * @param {DataSeries} rowData
   */
  init: function (jsonStatReader, centerPoints, rowData) {
    this._reader = jsonStatReader;
    this.layer = this._get(centerPoints, rowData);

    return this;
  },

  /**
   * Create and return the circle layer.
   * @param {GeoJSON} centerPoints
   * @param {DataSeries} rowData
   * @return {VectorLayer}
   * @private
   */
  _get (centerPoints, rowData) {
    return new VectorLayer({
      source: new VectorSource({
        features: this._getFeatures(centerPoints)
      }),
      zIndex: config.layerZIndex.circle,
      style: (feature) => this._getStyle(feature, rowData)
    });
  },

  /**
   * Return the label points as map features.
   * @param {GeoJSON} centerPoints
   * @return {Feature[]}
   * @private
   */
  _getFeatures (centerPoints) {
    const features = [];

    centerPoints.features.forEach(region => {
      const regionId = region.properties.regionid;
      const pointFeature = new Feature({
        geometry: new Point(region.geometry.coordinates)
      });
      pointFeature.setId(regionId);  // observable property
      features.push(pointFeature);
    });

    return features;
  },

  /**
   * Update the circles on the layer.
   * @returns {void}
   * @param {DataSeries} rowData
   */
  update (rowData) {
    if (this.layer !== null) {
      this.layer.setStyle(feature => {
        return this._getStyle(feature, rowData);
      });
    }
  },

  /**
   * Create and return the circle style.
   * @param {Feature} feature
   * @param {DataSeries} rowData
   * @return {Style|false}
   * @private
   */
  _getStyle (feature, rowData) {
    const item = rowData.series.find(region => Number(region.id) === feature.getId());
    if (item === undefined) { // a restriction on the region might be set
      return false;
    }
    const circle = config.color.circle;
    return new Style({
      image: new CircleStyle({
        stroke: new Stroke({
          color: item.value < 0 ? circle.negStroke : circle.posStroke,
          width: 1
        }),
        fill: new Fill({
          color: chroma(item.value < 0 ? circle.negFill : circle.posFill).alpha(circle.opacity).hex()
        }),
        radius: item.radius
      })
    });
  }
};